import { useSubmitFeedback } from '@doneverse/data/hooks'
import type { FeedbackImpact, ProductFeedbackType } from '@doneverse/shared'
import { zodResolver } from '@hookform/resolvers/zod'
import { ArrowLeft, Minus } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { FeedbackAttachmentField } from './FeedbackAttachmentField'
import {
  FEEDBACK_FORM_VERSION,
  FEEDBACK_LABEL_MAX_LENGTH,
  feedbackFormCopy,
  supportRedirectCopy,
} from './feedbackForms'
import { FeedbackTypePicker } from './FeedbackTypePicker'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { LoadingButton } from '@/components/ui/loading-button'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Textarea } from '@/components/ui/textarea'
import { useAuth } from '@/contexts/AuthContext'
import { FEEDBACK_IMPACT_META, FEEDBACK_IMPACT_OPTIONS } from '@/lib/feedback-display'
import { handlePasteFiles, nameClipboardFiles } from '@/lib/paste-files'
import { posthog } from '@/lib/posthog'

const feedbackSchema = z.object({
  label: z
    .string()
    .trim()
    .min(1, 'Please give this a one-line summary')
    .max(FEEDBACK_LABEL_MAX_LENGTH, `Keep this under ${FEEDBACK_LABEL_MAX_LENGTH} characters`),
  description: z.string().trim().min(1, 'Please tell us a bit more'),
  impact: z.string().optional(),
})

type FeedbackFormValues = z.infer<typeof feedbackSchema>

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmitted?: (feedbackId: string) => void
  /**
   * Fires when a draft starts existing and when it stops, so the launcher can
   * offer a minimised one back. Only the two ends matter — not every keystroke.
   */
  onDraftChange?: (hasDraft: boolean) => void
}

/**
 * Type picker → the chosen form, in a dialog wherever the user is.
 *
 * Submitting in place is deliberate: a bug report is worth most with the page it
 * happened on and a screenshot of it, and both are lost if we make people
 * navigate away first. Threads live on /feedback, where replies have the scroll
 * space a pop-up cannot give them.
 *
 * Which is exactly why this can be minimised. It is a modal over an 80%-black
 * overlay, so while it's open the reporter cannot see, scroll or screenshot the
 * page they're reporting on — and a founder hit that mid-report, unable to
 * capture the layout bug he was describing. Minimise hands the page back and
 * keeps the draft; closing keeps it too, because nothing here should cost
 * someone the report they already typed. Only Discard and a successful send
 * clear it.
 */
export function FeedbackFormDialog({ open, onOpenChange, onSubmitted, onDraftChange }: Props) {
  const { profile } = useAuth()
  const [type, setType] = useState<ProductFeedbackType | null>(null)
  const [files, setFiles] = useState<File[]>([])

  const form = useForm<FeedbackFormValues>({
    resolver: zodResolver(feedbackSchema),
    // `impact` starts as '' rather than undefined so the RadioGroup is
    // controlled from first render instead of flipping mid-life.
    defaultValues: { label: '', description: '', impact: '' },
  })

  /** Picking a type is where a draft begins — the picker alone is nothing to keep. */
  const selectType = (next: ProductFeedbackType) => {
    setType(next)
    onDraftChange?.(true)
  }

  /** Throws the draft away and returns to the picker. */
  const clearDraft = () => {
    setType(null)
    setFiles([])
    form.reset()
    onDraftChange?.(false)
  }

  const discardDraft = () => {
    clearDraft()
    onOpenChange(false)
  }

  const submitFeedback = useSubmitFeedback({
    onSuccess: (feedbackId) => {
      onOpenChange(false)
      clearDraft()
      onSubmitted?.(feedbackId)
    },
  })

  const copy = type ? feedbackFormCopy(type) : null
  const impactOptions = type ? FEEDBACK_IMPACT_OPTIONS[type] : []
  const supportRedirect = supportRedirectCopy(profile?.role)

  const onSubmit = (values: FeedbackFormValues) => {
    if (!type) return
    // Required on the two structured forms, never captured on general feedback.
    if (impactOptions.length > 0 && !values.impact) {
      form.setError('impact', { message: 'Please pick one' })
      return
    }

    submitFeedback.mutate({
      type,
      label: values.label.trim(),
      description: values.description.trim(),
      impact: (values.impact as FeedbackImpact | undefined) || null,
      form_version: FEEDBACK_FORM_VERSION,
      files,
      page_url: window.location.href,
      session_id: posthog.get_session_id?.() ?? undefined,
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          {/* Clears the two controls parked at the top-right (minimise at right-10,
              the dialog's own close at right-4) so a title that wraps — "Report
              Feedback & Issues" does, on a phone — never runs underneath them. */}
          <DialogTitle className="flex items-center gap-2 pr-16">
            {copy && (
              <button
                type="button"
                onClick={clearDraft}
                className="-ml-1 rounded p-1 hover:bg-muted"
                aria-label="Back to feedback types"
              >
                <ArrowLeft className="h-4 w-4" />
              </button>
            )}
            {copy ? copy.formTitle : 'Report Feedback & Issues'}
          </DialogTitle>
          <DialogDescription>
            {copy ? copy.formSubtitle : 'What kind of feedback do you have?'}
          </DialogDescription>
        </DialogHeader>

        <DialogBody>
          {!copy || !type ? (
            <div className="space-y-4">
              <FeedbackTypePicker onSelect={selectType} />
              {supportRedirect && <p className="text-xs text-muted-foreground">{supportRedirect}</p>}
            </div>
          ) : (
            <Form {...form}>
              <form id="feedback-form" onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="label"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{copy.labelQuestion}</FormLabel>
                      <FormControl>
                        <Input
                          placeholder={copy.labelPlaceholder}
                          maxLength={FEEDBACK_LABEL_MAX_LENGTH}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{copy.descriptionQuestion}</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder={copy.descriptionPlaceholder}
                          rows={5}
                          {...field}
                          // An OS screenshot goes to the clipboard, not to disk.
                          // Without this the reporter has to save a PNG somewhere
                          // just to hand it back through a file picker.
                          onPaste={handlePasteFiles((pasted) =>
                            setFiles((current) => [
                              ...current,
                              ...nameClipboardFiles(pasted, 'Screenshot', new Date()),
                            ]),
                          )}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {copy.impactQuestion && (
                  <FormField
                    control={form.control}
                    name="impact"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{copy.impactQuestion}</FormLabel>
                        <FormControl>
                          <RadioGroup value={field.value} onValueChange={field.onChange} className="gap-2">
                            {impactOptions.map((option) => (
                              <label
                                key={option}
                                className="flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm has-[button[data-state=checked]]:border-primary has-[button[data-state=checked]]:bg-primary/5"
                              >
                                <RadioGroupItem
                                  value={option}
                                  aria-label={FEEDBACK_IMPACT_META[option].label}
                                />
                                <span>{FEEDBACK_IMPACT_META[option].label}</span>
                              </label>
                            ))}
                          </RadioGroup>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                <div className="space-y-2">
                  <FeedbackAttachmentField value={files} onChange={setFiles} />
                  <p className="text-xs text-muted-foreground">
                    Need to see the page? Minimize this — your draft is kept. You can paste a screenshot
                    straight in.
                  </p>
                </div>

                {supportRedirect && <p className="text-xs text-muted-foreground">{supportRedirect}</p>}
              </form>
            </Form>
          )}
        </DialogBody>

        {copy && (
          <DialogFooter>
            {/* The only thing that throws a draft away on purpose. Low emphasis,
                and away from the two buttons people actually aim for. */}
            <Button type="button" variant="ghost" className="sm:mr-auto" onClick={discardDraft}>
              Discard
            </Button>
            <Button type="button" variant="outline" onClick={clearDraft}>
              Back
            </Button>
            <LoadingButton
              type="submit"
              form="feedback-form"
              // isPending covers the screenshot upload too — it happens inside
              // the mutation, so Send cannot outrun it.
              loading={submitFeedback.isPending}
              loadingText="Sending..."
            >
              Send
            </LoadingButton>
          </DialogFooter>
        )}

        {/* Sits beside the dialog's own close button (at right-4), and last in the
            DOM for the same reason that one is: neither should be what a keyboard
            lands on when the dialog opens. Both keep the draft; this one says so,
            which is the point — the reporter has to be able to tell that getting
            the page back is safe. */}
        <button
          type="button"
          onClick={() => onOpenChange(false)}
          className="absolute right-10 top-4 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
          title="Minimize — your draft is kept"
          aria-label="Minimize feedback form"
        >
          <Minus className="h-4 w-4" />
        </button>
      </DialogContent>
    </Dialog>
  )
}
